import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { LucideIcon } from "../components/LucideIcon";
import { useContactData } from "../hooks/useContactData";

export const HowItWorks = () => {
  const contactData = useContactData();
  const steps = contactData.howItWorks || [];

  return (
    <>
      <SEO
        title="How It Works"
        description="See how professional firms onboard to OOMS — from client import and task automation to WhatsApp broadcasts and billing."
        canonicalUrl={`${contactData.websiteUrl}/how-it-works`}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "How It Works", item: "/how-it-works" },
        ]}
      />

      <section className="pt-20 pb-16 text-center px-4 max-w-4xl mx-auto space-y-5 bg-background">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-primary-600 dark:text-primary-400 font-heading">
          Product Journey
        </span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl text-foreground leading-[0.95] tracking-tighter">
          From Spreadsheets to a Running Practice OS
        </h1>
        <p className="text-text-sub text-lg leading-relaxed font-medium">
          A clear path for CAs, CSs, CMAs, and tax firms to digitize clients,
          workflows, communications, and collections — usually within a day.
        </p>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="space-y-6">
          {steps.map((step, idx) => (
            <Reveal key={step.step} delay={idx * 0.06}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start rounded-[32px] border border-border bg-surface p-8 sm:p-10 shadow-xl shadow-foreground/5">
                <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4">
                  <span className="font-heading font-black text-4xl text-primary-600/20">
                    {step.step}
                  </span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
                    <LucideIcon name={step.iconName} className="h-6 w-6" />
                  </div>
                </div>
                <div className="md:col-span-10 space-y-3">
                  <h2 className="font-heading font-black text-2xl text-foreground tracking-tight">
                    {step.title}
                  </h2>
                  <p className="text-text-sub text-base leading-relaxed font-medium max-w-3xl">
                    {step.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted border-y border-border">
        <div className="max-w-7xl mx-auto space-y-10">
          <Reveal className="text-center space-y-4 max-w-2xl mx-auto">
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-foreground">
              Modules You Activate Along the Way
            </h2>
            <p className="text-text-sub font-medium">
              Every step maps to production-ready OOMS capabilities.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {contactData.services.slice(0, 6).map((service, idx) => (
              <Reveal key={service.id} delay={idx * 0.05}>
                <Link
                  to={service.path}
                  className="block rounded-[28px] border border-border bg-surface p-7 hover:border-primary-500 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
                    <LucideIcon name={service.iconName} className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading font-black text-lg text-foreground mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-text-sub font-medium leading-relaxed">
                    {service.shortDesc}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 text-center">
        <Reveal className="max-w-3xl mx-auto space-y-6">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-foreground">
            Ready to walk through it live?
          </h2>
          <p className="text-text-sub font-medium text-lg">
            Book a demo or explore pricing — no per-user fees, unlimited branches.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary-600 px-8 py-4 font-heading font-black text-white hover:bg-primary-700"
            >
              Schedule Demo <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-2xl border border-border bg-surface px-8 py-4 font-heading font-black text-foreground hover:bg-surface-hover"
            >
              View Pricing
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default HowItWorks;
